import { normalizeKey } from "../utils/normalizeDietKey";

function optionNum(titleOrTab) {
  return String(titleOrTab || "").match(/\d+/)?.[0] || "1";
}

function mealMatchesPeriod(meal, periodName) {
  const labels = [
    meal?.meal_type_description,
    meal?.meal_type,
    meal?.meal_name,
    meal?.name,
  ];
  const want = normalizeKey(periodName || "");
  return labels.some((label) => label && normalizeKey(label) === want);
}

function optionTitle(option) {
  return (
    option?.title ||
    option?.option_title ||
    option?.meal_option_title ||
    ""
  );
}

function foodKey(food) {
  return food?.localId || food?.deleteId || food?.id || null;
}

/**
 * Index of `food` inside one meal option's `foods`.
 *
 * The two copies of this list key rows differently. A card row born in this
 * session keeps its client uuid `localId` forever so its React key never
 * changes, but the tree row it corresponds to is replaced by a refetch and then
 * carries only `id`. Match the exact key first, then the server row id, then
 * food_id — the server keys one row per food per meal option, so food_id is a
 * valid identity *within* an option and is the bridge between the two
 * representations. Matching on localId alone appended a duplicate instead of
 * replacing, which is why updating a food produced a second identical card.
 */
function findFoodIndex(foods = [], food) {
  if (!food) return -1;

  const key = foodKey(food);
  if (key) {
    const byKey = foods.findIndex((f) => String(foodKey(f)) === String(key));
    if (byKey >= 0) return byKey;
  }

  const rowId = food.deleteId ?? food.id;
  if (rowId != null) {
    const byRowId = foods.findIndex(
      (f) =>
        (f.deleteId ?? f.id) != null &&
        String(f.deleteId ?? f.id) === String(rowId)
    );
    if (byRowId >= 0) return byRowId;
  }

  if (food.food_id != null) {
    return foods.findIndex((f) => String(f.food_id) === String(food.food_id));
  }
  return -1;
}

/** Merge an incoming (possibly optimistic) row onto the tree row it matched. */
function mergeFoodRow(prevRow, food) {
  const next = { ...prevRow, ...food };
  // An optimistic row whose create hasn't confirmed carries id/deleteId = null,
  // and copy-to-option clears them outright. Never let that erase a server id
  // the tree already knows — buildCardRowFromFood drops any row without one.
  // Tree rows only ever carry `id`, card rows carry both, so fall back across
  // the pair rather than field-for-field.
  const knownId = prevRow.id ?? prevRow.deleteId;
  if (food.id == null && knownId != null) next.id = knownId;
  if (food.deleteId == null && knownId != null) next.deleteId = knownId;
  return next;
}

/** Find a food in meals by period/option/key (for calorie delta on update). */
export function findFoodInMeals(meals = [], { periodName, optionTab, food }) {
  if (!food || !periodName) return null;
  const wantOpt = optionNum(optionTab);
  for (const meal of meals || []) {
    if (!mealMatchesPeriod(meal, periodName)) continue;
    for (const option of meal.meal_options || []) {
      if (optionNum(optionTitle(option)) !== wantOpt) continue;
      const foods = option.foods || [];
      const idx = findFoodIndex(foods, food);
      if (idx >= 0) return foods[idx];
    }
  }
  return null;
}
export function upsertFoodInMeals(meals = [], { periodName, optionTab, food }) {
  if (!food || !periodName) return meals;
  const wantOpt = optionNum(optionTab);
  let mealTouched = false;

  const next = (meals || []).map((meal) => {
    if (!mealMatchesPeriod(meal, periodName)) return meal;
    mealTouched = true;
    const options = meal.meal_options || [];
    let optionTouched = false;

    const nextOptions = options.map((option) => {
      if (optionNum(optionTitle(option)) !== wantOpt) return option;
      optionTouched = true;
      const foods = option.foods || [];
      const idx = findFoodIndex(foods, food);
      if (idx >= 0) {
        const nextFoods = foods.slice();
        nextFoods[idx] = mergeFoodRow(nextFoods[idx], food);
        return { ...option, foods: nextFoods };
      }
      return { ...option, foods: [...foods, food] };
    });

    if (optionTouched) {
      return { ...meal, meal_options: nextOptions };
    }

    // Meal matched but no option with this number — append one so calories move.
    return {
      ...meal,
      meal_options: [
        ...options,
        {
          title: `Option ${wantOpt}`,
          foods: [food],
        },
      ],
    };
  });

  return mealTouched ? next : meals;
}

/**
 * Remove a food from one meal option.
 *
 * Uses the same identity ladder as the upsert: a session row's `localId` is not
 * in the tree once a refetch has replaced it with server rows, so falling back
 * to `rowId`/`foodId` is what actually removes it. Matching on `localId` alone
 * removed nothing and left a ghost that reappeared on the next merge.
 */
export function removeFoodFromMeals(
  meals = [],
  { periodName, optionTab, localId, rowId, foodId = null }
) {
  if (!periodName) return meals;
  const wantOpt = optionNum(optionTab);
  if (localId == null && rowId == null && foodId == null) return meals;
  const target = { localId, id: rowId, deleteId: rowId, food_id: foodId };

  return meals.map((meal) => {
    if (!mealMatchesPeriod(meal, periodName)) return meal;
    return {
      ...meal,
      meal_options: (meal.meal_options || []).map((option) => {
        if (optionNum(optionTitle(option)) !== wantOpt) return option;
        const foods = option.foods || [];
        const idx = findFoodIndex(foods, target);
        if (idx < 0) return option;
        const nextFoods = foods.slice();
        nextFoods.splice(idx, 1);
        return { ...option, foods: nextFoods };
      }),
    };
  });
}
